import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

//Hooks
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

//Helpers
import { checkDeviceData, completeColumns } from "Core/data/Helpers";
import { getWarningCategory } from "Core/hooks/utils";
import { HasPermission } from "Core/security";
import { useRealtimeUnits } from 'Core/hooks';

//Components
import { NotificationManager } from "Components/Notifications";
import { Grid, Icon } from "@mui/material";
import Preferences from "Core/containers/Preferences";

//Controls
import {
  TableView,
  SearchInput,
  TableButtons,
  Switch,
  Tooltip,
  Pill,
  useTablePagination,
  MenuComponent
} from "@gpstrackit/gtc-react-controls";

//Actions
import { cameraManagementSearchUnits, reset } from "./redux/slice";
import {
  sendGenericExport,
  clearRedux,
  openWarningsModal,
  openModalCameraSetup,
  savePreference
} from "Redux/actions";
import { openModalEditCameraManagementPairing } from "./modals/editCameraManagementPairing/redux/slice";
import { openModalEditCameraManagementCamera } from "./modals/editCameraManagementCamera/redux/slice";

//Styles
import "./styles.scss";

//Modals
import EditCameraManagementPairing from "./modals/editCameraManagementPairing";
import EditCameraManagementCamera from "./modals/editCameraManagementCamera";

const KEY_PREFERENCE = "Manage-CameraManagementTab-list";
const INITIAL_SORT = { key: "label", order: "asc" };

const INITIAL_CONDITIONS = [
  {
    fields: ["deviceModel_manufacturer_name"],
    terms: ["VidFleet", "SmartWitness", "LightMetrics"],
    exact: true
  },
  {
    fields: ["linkedMode"],
    terms: ["1", "0"],
    exact: true
  },
]

const CONDITION_UNPAIRED_ONLY = [
  {
    fields: ["linkedUnitId"],
    terms: ["NULL"],
    exact: true
  }
]

const CameraManageTab = (props) => {
  //States
  const [unpairedOnly, setUnpairedOnly] = useState(false);
  const [preferences, setPreferences] = useState({});
  const [loadPreferences, setLoadPreferences] = useState(true);
  const [loadColumns, setLoadColumns] = useState(false);

  //Language
  const { messages, locale } = useIntl();

  //Table refs
  const tableRef = useRef();
  const isFirstRender = useRef(true);

  //Redux
  const authenticatedId = useSelector((state) => state.securityRedux.authenticatedId);
  const dispatch = useDispatch();
  const { loading, items, total } = useSelector(
    (state) => state.cameraManagementTabRedux
  );
  const openEditCameraManagementPairing = useSelector(
    (state) => state.editCameraManagementPairingRedux.open
  );
  const openEditCameraManagementCamera = useSelector(
    (state) => state.editCameraManagementCameraRedux.open
  );
  const exportResult = useSelector((state) => state.commomRedux.exportResult);
  const exportName = useSelector((state) => state.commomRedux.exportName);
  const resultResolveMaintenanceWarningEvent = useSelector(
    (state) => state.warningsConfirmResolveRedux.result
  );
  const user = useSelector((state) => state.securityRedux.user);
  const catalogUnits = useRealtimeUnits({ trackFields: ['id', 'name', 'label', 'videoDevices'] });

  if (exportResult && exportName === "camera_management") {
    NotificationManager.info(
      messages["exportInProcessBody"],
      messages["exportInProcessTitle"],
      4000,
      null,
      null,
      "info",
      null
    );
    dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
  }

  //Hooks
  const pagination = useTablePagination({
    dispatch,
    action: cameraManagementSearchUnits,
    searchFields: ["label", "linkedUnit_label", "esn"],
    tableRef,
    exportName: "camera_management",
    sendGenericExport,
    termsFields: unpairedOnly ? [...CONDITION_UNPAIRED_ONLY, ...INITIAL_CONDITIONS] : [...INITIAL_CONDITIONS]
  });

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (unpairedOnly) {
      pagination.addConditions(1, [
        { fields: ["linkedUnitId"], terms: ["NULL"], exact: true }
      ]);
    } else {
      pagination.addConditions(1, []);
    }
    tableRef?.current?.setPage(1);
  }, [unpairedOnly]);

  //Memos
  const currentColumns = useMemo(
    () => [
      {
        key: "maintenanceWarningEventsCount",
        title: "",
        show: false,
        export: false,
        sort: false,
        width: 25,
        render: (item) => {
          //Getting information from the SD Card
          const sdCard = item?.sdCard ? checkDeviceData(item.sdCard) : null;
          const sdCardStatus = sdCard?.status?.toUpperCase();
          const isSdCardError = sdCardStatus && sdCardStatus !== "NORMAL";

          //Getting warnings
          const warnings = item?.maintenanceWarningEventsCount || 0;
          //Check if the device is offline
          const isOffline = item?.isOffline;

          const { title, color } = getWarningCategory(isSdCardError, warnings, isOffline, messages);
          if (!title) return <></>;
          return (
            <Tooltip title={title}>
              <Icon
                style={{
                  marginLeft: 5,
                  cursor: HasPermission(user, "wgrs", [278]) && "pointer",
                  color: color
                }}
                className="toolbar-list-icon fs-20"
                onClick={() => {
                  if (HasPermission(user, "wgrs", [278])) {
                    dispatch(openWarningsModal(item));
                  }
                }}
              >
                warning
              </Icon>
            </Tooltip>
          );
        }
      },
      {
        key: "label",
        title: messages["manage_cameraManagement_label"],
        show: true,
        export: true,
        sort: true,
        width: 200,
        render: (row) => {
          let options = [messages["manage_cameraManagement_editCamera"]];
          if (row?.linkedMode) {
            options.push(messages["manage_cameraManagement_editCameraPairing"]);
          }
          if (
            row?.deviceModel?.manufacturer?.name?.toUpperCase() === "VIDFLEET" &&
            HasPermission(user, "vfsetup", [270])
          ) {
            options.push(messages["manage_cameraManagement_cameraSetup"]);
          }
          return (
            <Grid container alignItems="center">
              <Grid item xs={11} zeroMinWidth style={{ width: 300 }}>
                <span className="table-span">{row.label}</span>
              </Grid>
              <Grid item xs={1}>
                <MenuComponent
                  isDisable={loading}
                  options={options}
                  placement="right-start"
                  icon="more_vert"
                  className="showInHover"
                  onClick={(type) => {
                    if (type === messages["manage_cameraManagement_editCameraPairing"]) {
                      dispatch(openModalEditCameraManagementPairing({ open: true, unit: row }));
                    } else if (type === messages["manage_cameraManagement_editCamera"]) {
                      dispatch(openModalEditCameraManagementCamera({ open: true, unit: row }));
                    } else if (type === messages["manage_cameraManagement_cameraSetup"]) {
                      dispatch(
                        openModalCameraSetup({
                          open: true,
                          units: [
                            {
                              unitId: row?.id,
                              unitLabel: row.label,
                              esn: row?.esn,
                              deviceModel: row?.deviceModel?.name
                            }
                          ]
                        })
                      );
                    }
                  }}
                  value={row}
                />
              </Grid>
            </Grid>
          );
        },
        functionString: "return row?.label"
      },
      {
        key: "device_model",
        title: messages["manage_cameraManagement_device_model"],
        show: true,
        export: true,
        render: (row) => {
          return (
            <span>
              {messages[`manage_cameraManagement_${row.deviceModel?.name}`] ||
                row.deviceModel?.name}
            </span>
          );
        },
        functionString: `
                  if(row?.deviceModel?.name === "4K"){
                      return "VidFleet AI+"
                  }else if(row?.deviceModel?.name === "360"){
                      return "VidFleet 1"
                  }
                  return row?.deviceModel?.name
              `
      },
      {
        key: "esn",
        title: messages["manage_cameraManagement_esn"],
        show: true,
        export: true,
        sort: true,
        render: (row) => {
          return <span>{row.esn}</span>;
        },
        functionString: "return row?.esn"
      },
      {
        key: "paired_unit",
        title: messages["manage_cameraManagement_pairedUnit"],
        show: true,
        export: true,
        render: (row) => {

          const findUnit = catalogUnits?.find(unit => unit?.id === row?.linkedUnitId);

          if (!row?.linkedMode) {
            return (
              <Pill
                style={{ width: 110 }}
                item={{
                  label: messages["manage_cameraManagement_camara_as_gps"],
                  color: "#B4B4B4"
                }}
                variant={"outlined"}
              />
            );
          }
          if (!row.linkedUnitId) {
            return <span>{messages["manage_cameraManagement_unpaired"]}</span>;
          }
          return <span>{findUnit?.label}</span>;
        },
        exportValue: (row) => {
          return Number(row?.linkedMode) === 1
            ? row?.linkedUnit?.label || messages["manage_cameraManagement_unpaired"]
            : messages["manage_cameraManagement_camara_as_gps"];
        },
        functionString: `if (Number(row?.linkedMode) === 1) {
            return row?.linkedUnit?.label || "${messages["manage_cameraManagement_unpaired"]}";
          } else {
            return "${messages["manage_cameraManagement_camara_as_gps"]}";
          }`
      }
    ],
    [locale, user]
  );

  //Handlers
  const savePreferences = useCallback(
    (preferencesArray) => {
      if (!Array.isArray(preferencesArray)) {
        console.error("Expected an array of preferences");
        return;
      }

      let updatedPreferences = { ...preferences } || {};

      preferencesArray.forEach(({ key, value }) => {
        updatedPreferences[key] = value;
      });

      setPreferences(updatedPreferences);

      dispatch(
        savePreference({
          entityName: "User",
          entityId: authenticatedId,
          preferenceKey: KEY_PREFERENCE,
          preferenceValue: updatedPreferences
        })
      );
    },
    [preferences]
  );

  //Effects
  useEffect(() => {
    if (preferences?.columns?.length && !loadColumns) {
      const preferenceMap = new Map(preferences.columns.map((col) => [col.key, col]));

      // Reorder columns based on preferences (keeping `show`)
      const reorderedColumns = preferences.columns
        .map((pref) => {
          const col = currentColumns.find((c) => c.key === pref.key);
          return col ? { ...col, show: pref.show } : null;
        })
        .filter(Boolean);

      // Add new columns (not listed in preferences), with `show: true` by default
      const newColumns = currentColumns
        .filter((col) => !preferenceMap.has(col.key))
        .map((col) => ({ ...col, show: true }));

      const finalColumns = [...reorderedColumns, ...newColumns];

      pagination.setColumns(finalColumns);
      setPreferences({ ...preferences });
      setLoadColumns(true);
    }
  }, [preferences.columns, loadColumns]);

  useEffect(() => {
    if (pagination.columns.length > 0) {
      setPreferences({ ...preferences, columns: pagination.columns });
      setLoadColumns(false);
    }
  }, [locale]);

  useEffect(() => {
    pagination.setColumns(currentColumns);
  }, [currentColumns]);

  useEffect(() => {
    if (resultResolveMaintenanceWarningEvent?.resolve) {
      pagination && pagination?.refresh();
    }
  }, [resultResolveMaintenanceWarningEvent]);

  return (
    <div>
      <Grid container direction="row" justifyContent="space-between" alignItems="center">
        <Grid item xs={4}>
          <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            spacing={1}
          >
            <Grid item style={{ display: "flex" }}>
              <TableButtons
                messages={messages}
                tableRef={tableRef}
                buttons={["export", "orderColumn"]}
                {...pagination}
                onChangeColumns={(columns) => {
                  savePreferences([{ key: "columns", value: columns }]);
                  pagination.setColumns(columns);
                }}
              />
              {HasPermission(user, "vfsetup", [270]) ? (
                <MenuComponent
                  disabled={loading}
                  options={[
                    {
                      name: messages["manage_cameraManagement_bulk_camera_config"]
                    }
                  ]}
                  placement="right"
                  icon="more_vert"
                  onClick={(type) => {
                    if (type.name !== messages["manage_cameraManagement_bulk_camera_config"])
                      return;
                    dispatch(openModalCameraSetup({ open: true, units: [], bulk: true }));
                  }}
                />
              ) : (
                <></>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid
          item
          xs={8}
          className="mb-10 text-right"
          style={{ display: "flex", alignItems: "center" }}
        >
          <div style={{ width: 500, display: "flex", alignItems: "center", justifyContent: "end" }}>
            <span style={{ marginRight: 5 }}>
              {messages["manage_cameraManagement_showUnpairedOnly"]}
            </span>
            <Switch
              id="unpaired-only"
              name="unpaired-only"
              backgroundColorClass="gtc-rc-blue-switch"
              optionLabels={["", ""]}
              checked={unpairedOnly}
              onChange={(value) => setUnpairedOnly(value)}
              isDisabled={loading}
              size="small"
              labelPosition="left"
              style={{ marginRight: 5 }}
            />
          </div>
          <SearchInput
            placeholder={messages["manage_cameraManagement_search_by_label_imei_esn"]}
            searchText={pagination.searchText}
            onSearchText={pagination.onSearchChange}
          />
        </Grid>
      </Grid>
      <Preferences
        preferencesKey={KEY_PREFERENCE}
        onInitialPreferences={(data) => {
          if (!loadPreferences) return;
          setTimeout(() => {
            let sort = data?.sort || INITIAL_SORT; // * Initial sort
            tableRef.current?.setSort?.(sort);
            pagination.onChangeSort(sort);
          }, 100);
          setPreferences(data);
          setLoadPreferences(false);
        }}
      >
        <TableView
          keyColumn="id"
          data={items}
          total={total || 0}
          loading={loading || loadPreferences}
          hideAllInPagination={true}
          infiniteScroll={false}
          topheight={250}
          disableItemSelection
          {...pagination}
          onChangeColumns={(columns) => {
            const newcurrentColumns = pagination?.columns || [];
            const finalsColumns = completeColumns(newcurrentColumns, columns);
            savePreferences([{ key: "columns", value: finalsColumns }]);
            pagination.onChangeColumns(finalsColumns);
          }}
          onChangeSort={(newSort) => {
            savePreferences([{ key: "sort", value: newSort }]);
            pagination.onChangeSort(newSort);
          }}
        />
      </Preferences>
      {openEditCameraManagementPairing && (
        <EditCameraManagementPairing refresh={tableRef?.current?.refreshTable} />
      )}
      {openEditCameraManagementCamera && (
        <EditCameraManagementCamera refresh={() => {
          pagination.onSearchChange('')
        }} />
      )}
    </div>
  );
};

export default CameraManageTab;
